import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: '1rem',
    padding: '1rem'
  },
  cardDiv: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  cardActionArea: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(2)
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0, 0, 0)
  },
  mandatoryCheckbox: {
    marginLeft: 'auto'
  },
  typography: {
    marginBottom: theme.spacing(1)
  },
  cardContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: 0
  },
  media: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius
  },
  imageContainer: {
    // flex: '0 0 140px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  fieldGroup: {
    marginRight: theme.spacing(1)
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  }
}));

const QualificationCard = ({
  qualification,
  isEditing,
  editData,
  onEdit,
  onSave,
  onCancel,
  onFieldChange,
  onFileChange,
  loading,
  mandatoryQualifications
}) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <Typography gutterBottom variant="h6" component="div">
        {qualification.name}
      </Typography>

      <div className={classes.cardDiv}>
        {/* Image Section */}
        <div className={classes.imageContainer}>
          {isEditing && qualification.cardNumber ? (
            <>
              {editData.previewUrl && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    const isPdf =
                      // eslint-disable-next-line no-undef
                      editData.qualificationFile instanceof File
                        ? editData.qualificationFile.type === 'application/pdf'
                        : typeof editData.previewUrl === 'string' && editData.previewUrl.toLowerCase().endsWith('.pdf');

                    const fileUrl =
                      // eslint-disable-next-line no-undef
                      editData.qualificationFile instanceof File
                        ? // eslint-disable-next-line no-undef
                          URL.createObjectURL(editData.qualificationFile)
                        : editData.previewUrl;

                    if (isPdf) {
                      return (
                        <iframe
                          src={fileUrl}
                          width="100%"
                          height="100%"
                          title="Qualification Preview"
                          style={{ border: 'none' }}
                        />
                      );
                    }

                    return (
                      <img
                        src={fileUrl || '/placeholder.svg'}
                        name="qualificationFile"
                        alt="Qualification file"
                        className={classes.media}
                      />
                    );
                  })()}
                </div>
              )}
              <input
                type="file"
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                id={`qualification-upload-${qualification.qualificationId}`}
                onChange={onFileChange}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor={`qualification-upload-${qualification.qualificationId}`}
                sx={{ mt: 2 }}
                fullWidth
              >
                Upload Qualification
              </Button>
              {editData.qualificationFile && typeof editData.qualificationFile !== 'string' && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                  Selected: {editData.qualificationFile.name}
                </Typography>
              )}
            </>
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              {(() => {
                const isPdf =
                  typeof qualification?.fileContent === 'string' &&
                  qualification.fileContent.toLowerCase().endsWith('.pdf');

                if (isPdf) {
                  return (
                    <iframe
                      src={qualification.fileContent}
                      width="100%"
                      height="100%"
                      title="Qualification PDF"
                      style={{ border: 'none' }}
                    />
                  );
                }

                return (
                  <CardMedia
                    component="img"
                    alt="Qualification Image"
                    height="140"
                    image={qualification?.fileContent}
                    title="Qualification Image"
                    className={classes.media}
                  />
                );
              })()}
            </div>
          )}
        </div>

        {/* Content Section */}
        <CardContent className={classes.cardContent}>
          {/* Card Number */}
          <Box className={classes.fieldGroup}>
            <Typography variant="body2" color="textSecondary" className={classes.typography}>
              Card Number:
            </Typography>
            <TextField
              size="small"
              name="referenceNumber"
              fullWidth
              variant="outlined"
              value={isEditing ? editData.referenceNumber : qualification.cardNumber || ''}
              onChange={onFieldChange}
              disabled={!isEditing}
            />
          </Box>

          {/* Issue Date */}
          <Box className={classes.fieldGroup}>
            <Typography variant="body2" color="textSecondary" className={classes.typography}>
              Issue Date:
            </Typography>
            <TextField
              name="issueDate"
              type="date"
              fullWidth
              variant="outlined"
              value={isEditing ? editData.issueDate : qualification.issueDate || ''}
              onChange={onFieldChange}
              disabled={!isEditing}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Expiry Date */}
          <Box className={classes.fieldGroup}>
            <Typography variant="body2" color="textSecondary" className={classes.typography}>
              Expiry Date:
            </Typography>
            <TextField
              name="expiryDate"
              type="date"
              fullWidth
              variant="outlined"
              value={isEditing ? editData.expiryDate : qualification.expiryDate || ''}
              onChange={onFieldChange}
              disabled={!isEditing}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </CardContent>
      </div>

      {/* Actions Section */}
      <Box className={classes.cardActions}>
        {/* Edit/Save/Cancel Buttons */}
        <div className={classes.buttonGroup}>
          {isEditing ? (
            <>
              <Button size="small" color="primary" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button size="small" color="primary" variant="contained" onClick={onSave} disabled={loading}>
                Save
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() => onEdit(qualification)}
              disabled={loading}
            >
              Edit
            </Button>
          )}
        </div>

        <FormControlLabel
          className={classes.mandatoryCheckbox}
          control={
            <Checkbox
              checked={mandatoryQualifications.includes(qualification.name)}
              onChange={onFieldChange}
              name={qualification.name}
              color="primary"
            />
          }
          label="Mandatory"
        />
      </Box>
    </Card>
  );
};

QualificationCard.propTypes = {
  qualification: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
  editData: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  mandatoryQualifications: PropTypes.array
};

export default QualificationCard;
